import React, { useState, useEffect, FormEvent } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { Divider } from '@mui/material';
import useCreateBooking, { RoomDetail } from '../../../hooks/useCreateBooking';
import { useNavigate } from 'react-router-dom';
import { getReturnUrl } from '../helpers/paymentHelper';
import { trackPurchase } from '../../../utils/analytics';
import { clearPaymentDraft } from '../../../utils/paymentDraft';
import {
  firstCheckoutErrorMessage,
  validateCheckoutGuest,
} from '../../../utils/checkoutValidation';
import { savePendingStripeReturn } from '../../../utils/pendingStripeReturn';
import { buildThankYouPath } from '../../../utils/thankYouNavigation';

interface Props {
  tripId: number;
  selectedRooms: RoomDetail[];
  email: string;
  fullName: string;
  gender: string;
  phoneNumber: string;
  paymentOption: number;
  numberOfGuests: number;
  currency: string;
  boatId?: number;
  /** Known PaymentIntent id (from createPaymentIntent / client secret). */
  paymentIntentId?: string | null;
  /** Booking already created for this checkout (survives Elements remount). */
  existingBookingId?: number | null;
  onBookingCreated?: (bookingId: number) => void;
}

const SUPPORT_EMAIL = 'help@santtai.com';

const CheckoutForm: React.FC<Props> = ({
  tripId,
  selectedRooms,
  email,
  fullName,
  gender,
  phoneNumber,
  paymentOption,
  numberOfGuests,
  currency,
  boatId,
  paymentIntentId: initialPaymentIntentId,
  existingBookingId = null,
  onBookingCreated,
}) => {
  const stripe: Stripe | null = useStripe();
  const elements: StripeElements | null = useElements();
  const createBookingMutation = useCreateBooking();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [createdBookingId, setCreatedBookingId] = useState<number | null>(
    existingBookingId,
  );
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(
    initialPaymentIntentId ?? null,
  );

  useEffect(() => {
    // Sync both ways: parent restore after remount, and parent clear on new checkout.
    setCreatedBookingId(existingBookingId ?? null);
  }, [existingBookingId]);

  useEffect(() => {
    if (initialPaymentIntentId) {
      setPaymentIntentId(initialPaymentIntentId);
    }
  }, [initialPaymentIntentId]);

  const validateForm = () => {
    const errors = validateCheckoutGuest({
      fullName,
      email,
      phone: phoneNumber,
      gender,
    });
    const message = firstCheckoutErrorMessage(errors);
    if (message) {
      setErrorMessage(message);
      return false;
    }
    setErrorMessage(null);
    return true;
  };

  const navigateToThankYou = (
    bookingId: number,
    paymentSucceeded: boolean,
    confirmedPaymentIntentId?: string | null,
  ) => {
    clearPaymentDraft();
    const resolvedPaymentIntentId =
      confirmedPaymentIntentId ?? paymentIntentId ?? initialPaymentIntentId;
    const totalValue = selectedRooms.reduce(
      (sum, room) => sum + room.pricePerRoom * room.numberOfRooms,
      0,
    );
    const thankYouState = {
      bookingId,
      value: totalValue,
      currency,
      paymentPath: 'stripe' as const,
      tripId,
      boatId,
      paymentSucceeded,
      paymentIntentId: resolvedPaymentIntentId,
    };

    const doNavigate = () => {
      // Query params keep booking context across refresh; state carries value/analytics.
      navigate(buildThankYouPath(thankYouState), { state: thankYouState });
    };

    if (paymentSucceeded) {
      trackPurchase(
        {
          bookingId,
          value: totalValue,
          currency,
          paymentPath: 'stripe',
          tripId,
          boatId,
        },
        doNavigate,
      );
      setTimeout(doNavigate, 1000);
      return;
    }

    doNavigate();
  };

  const createOrAttachBooking = (
    intentId: string | null | undefined,
    bookingIdToAttach?: number | null,
  ): Promise<number> =>
    new Promise((resolve, reject) => {
      createBookingMutation.mutate(
        {
          tripId,
          bookingState: 1,
          rooms: selectedRooms,
          email,
          fullName,
          gender,
          phone: phoneNumber,
          paymentOption,
          numberOfGuests,
          paymentIntentId: intentId || undefined,
          existingBookingId: bookingIdToAttach || undefined,
        },
        {
          onSuccess: data => {
            const bookingId = data.createBooking.bookingId;
            setCreatedBookingId(bookingId);
            onBookingCreated?.(bookingId);
            resolve(bookingId);
          },
          onError: error => {
            reject(error);
          },
        },
      );
    });

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!elements || !stripe) {
      setErrorMessage('Payment system is not ready. Please try again.');
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    let bookingId = createdBookingId ?? existingBookingId ?? null;
    try {
      const knownPaymentIntentId =
        paymentIntentId || initialPaymentIntentId || null;

      // Create booking before charging so a failed mutation cannot leave a
      // diver charged without a booking row. If a coupon recreated the PI,
      // attach the new intent to the existing booking for reconciliation.
      if (bookingId) {
        await createOrAttachBooking(knownPaymentIntentId, bookingId);
      } else {
        bookingId = await createOrAttachBooking(knownPaymentIntentId);
      }

      const totalValue = selectedRooms.reduce(
        (sum, room) => sum + room.pricePerRoom * room.numberOfRooms,
        0,
      );

      // Persist context for Stripe 3DS redirects that land on /thankyou without router state.
      savePendingStripeReturn({
        bookingId,
        tripId,
        boatId,
        currency,
        value: totalValue,
        paymentPath: 'stripe',
        paymentIntentId: knownPaymentIntentId,
      });

      const returnUrl = getReturnUrl(window.location.hostname, {
        bookingId,
        tripId,
        boatId,
        paymentSucceeded: '1',
        paymentIntentId: knownPaymentIntentId,
      });
      const confirmation = await stripe.confirmPayment({
        elements,
        confirmParams: {
          receipt_email: email,
          return_url: returnUrl,
        },
        redirect: 'if_required',
      });

      if (confirmation.error) {
        setErrorMessage(
          `${confirmation.error.message || 'Payment failed.'} Your spot is reserved (Booking #${bookingId}). You can retry payment or email ${SUPPORT_EMAIL} with this booking ID.`,
        );
        setIsLoading(false);
        return;
      }

      const paymentIntent = confirmation.paymentIntent;
      if (paymentIntent?.status === 'succeeded') {
        setPaymentIntentId(paymentIntent.id);
        setIsLoading(false);
        navigateToThankYou(bookingId, true, paymentIntent.id);
        return;
      }

      const unresolvedPaymentIntentId =
        paymentIntent?.id ?? knownPaymentIntentId;
      setPaymentIntentId(unresolvedPaymentIntentId);
      setErrorMessage(
        `Payment was not completed. Your spot is reserved (Booking #${bookingId}). Contact ${SUPPORT_EMAIL} if you need help finishing payment.`,
      );
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      if (bookingId) {
        setCreatedBookingId(bookingId);
        setErrorMessage(
          `Booking #${bookingId} was created but something went wrong finishing payment. Contact ${SUPPORT_EMAIL} with this booking ID.`,
        );
      } else {
        setErrorMessage(
          'Failed to create booking. You have not been charged. Please try again.',
        );
      }
      setIsLoading(false);
    }
  };

  const handleContinueWithBooking = () => {
    if (!createdBookingId) {
      return;
    }
    navigateToThankYou(createdBookingId, false);
  };

  return (
    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMessage && (
        <div className="flex flex-col gap-2 text-red-500" role="alert">
          <div>{errorMessage}</div>
          {createdBookingId && (
            <div className="flex flex-wrap gap-3">
              <button
                type="submit"
                className="underline font-semibold text-neutral-800"
                disabled={isLoading}
              >
                Retry payment
              </button>
              <button
                type="button"
                className="underline font-semibold text-neutral-800"
                onClick={handleContinueWithBooking}
              >
                Continue with booking #{createdBookingId}
              </button>
              <a
                className="underline font-semibold text-neutral-800"
                href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
                  `Pay-now booking help #${createdBookingId}`,
                )}&body=${encodeURIComponent(
                  `Booking ID: ${createdBookingId}\nPayment Intent: ${paymentIntentId ?? 'n/a'}\n`,
                )}`}
              >
                Contact support
              </a>
            </div>
          )}
        </div>
      )}
      <Divider />
      <div className="flex flex-col items-start gap-2 self-stretch">
        <span className="text-neutral-800 font-semibold">
          Cancellation policy
        </span>
        <span className="text-neutral-500 text-sm">
          This booking is non-refundable. Once confirmed, payments are
          non-refundable under any circumstances. Please review your plans
          carefully before booking. For any inquiries, contact our support team.
        </span>
      </div>
      <Divider />
      <button
        className="flex py-4 px-6 items-start justify-center self-stretch rounded-lg bg-gradient-to-r from-brand-santtai-primary to-brand-santtai-primary-darker text-neutral-00 w-full"
        type="submit"
        disabled={!stripe || !elements || isLoading}
      >
        Confirm and pay
      </button>
      <span className="text-neutral-500 text-sm">
        After booking completion, we will collect specific dive-related
        information, including flight details, guest information, and rental
        diving gear requirements.
      </span>
      {isLoading && (
        <div className="fixed inset-0 flex items-center justify-center z-50">
          <div className="fixed inset-0 bg-black opacity-50"></div>
          <div className="bg-white rounded-lg shadow-lg p-6 z-50 w-80">
            <h2 className="text-lg font-semibold">Please wait...</h2>
            <p className="mt-2">
              We&apos;re currently processing your liveaboard trip.
            </p>
          </div>
        </div>
      )}
    </form>
  );
};

export default CheckoutForm;
